// The three staff doors on a lesson (spec 044 D9), each a dialog around one
// callable: Cancel as staff → staffCancelBooking, Mark finished → staffFinishLesson,
// Open ticket → staffCreateTicket. Credit / refund is AdjustBalanceDialog, unchanged.
// Each reports success through onDone(message) — the page shows it and reloads the
// lesson — and keeps an error inside the dialog, so a call that did not commit never
// reads as done. Every success also leaves an activityLogs row, as AdjustBalanceDialog
// does: the Activity Log is where "who settled this lesson" is answered later.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { RiMore2Line, RiMoreLine } from "@remixicon/react";
import {
  getUserById,
  staffCancelBooking,
  staffFinishLesson,
  staffCreateTicket,
} from "../../services/dataService";
import { hasPermission, permissions } from "../../config/permissions";
import AdjustBalanceDialog from "../../components/AdjustBalanceDialog/AdjustBalanceDialog";
import SmallButton from "../../components/kit/SmallButton";
import { RowAction } from "../../components/kit/TableParts";
import { logUserActivity } from "../../utils/activityLogger";
import { egp } from "../../utils/queuePresentation";
import {
  TICKET_CATEGORIES,
  REASON_MAX,
  TICKET_TEXT_MAX,
  staffActions,
} from "../../utils/lessonPresentation";

const actor = (user) => ({
  userId: user?.uid || user?.id || "unknown",
  userName: user?.name || user?.displayName || "Unknown User",
  userEmail: user?.email || "unknown@example.com",
});

const sessions = (n) => `${n} ${n === 1 ? "session" : "sessions"}`;

export const CancelAsStaffDialog = ({ open, onClose, booking, label, actingUser, onDone }) => {
  const [refund, setRefund] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const trimmed = reason.trim();
  const valid = trimmed.length > 0 && trimmed.length <= REASON_MAX;
  const reset = () => {
    setRefund(true);
    setReason("");
    setError("");
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };
  const submit = async () => {
    if (!valid || !booking) return;
    setBusy(true);
    setError("");
    try {
      const result = await staffCancelBooking({ lessonId: booking.id, refund, reason: trimmed });
      await logUserActivity({
        ...actor(actingUser),
        action: "LESSON_STAFF_CANCEL",
        entityType: "lesson",
        entityId: booking.id,
        entityName: label,
        before: { status: booking.status },
        after: { status: "Cancelled", refund, outcome: result?.outcome, reason: trimmed },
        description: `${actingUser?.name || "User"} cancelled ${label} as staff (${
          refund ? "refund" : "no refund"
        }): ${trimmed}`,
      });
      const back = result?.refunded
        ? ` ${sessions(result.refunded)} returned.`
        : result?.outcome === "freeSessionReturned"
          ? " Free session returned."
          : " Nothing returned.";
      reset();
      onClose();
      onDone(`Lesson cancelled.${back}`);
    } catch (e) {
      setError(e.message || "Could not cancel the lesson.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel as staff</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
        <DialogContentText>
          {label}. The tutor is not paid for a cancelled lesson; both sides are notified.
        </DialogContentText>
        <FormControlLabel
          control={
            <Switch checked={refund} onChange={(e) => setRefund(e.target.checked)} disabled={busy} />
          }
          label={
            booking?.isFreeSession
              ? "Return the free session"
              : "Return the session to the learner's wallet"
          }
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          disabled={busy}
          helperText={`${trimmed.length}/${REASON_MAX} — written on the lesson, where the learner and the tutor can read it; not an internal note`}
          error={trimmed.length > REASON_MAX}
          autoFocus
        />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Keep lesson
        </Button>
        <Button onClick={submit} variant="contained" color="error" disabled={!valid || busy}>
          {busy ? "Cancelling…" : refund ? "Cancel and refund" : "Cancel without refund"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const MarkFinishedDialog = ({ open, onClose, booking, label, actingUser, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const close = () => {
    if (busy) return;
    setError("");
    onClose();
  };
  const submit = async () => {
    if (!booking) return;
    setBusy(true);
    setError("");
    try {
      const result = await staffFinishLesson({ lessonId: booking.id });
      await logUserActivity({
        ...actor(actingUser),
        action: "LESSON_STAFF_FINISH",
        entityType: "lesson",
        entityId: booking.id,
        entityName: label,
        before: { status: booking.status },
        after: { status: "Finished", outcome: result?.outcome, earned: result?.earned },
        description: `${actingUser?.name || "User"} marked ${label} completed as staff`,
      });
      setError("");
      onClose();
      onDone(
        result?.outcome === "freeSessionFinished"
          ? "Lesson completed. Free session — the tutor is not paid."
          : `Lesson completed. ${egp(result?.earned)} added to the tutor's earnings.`
      );
    } catch (e) {
      setError(e.message || "Could not complete the lesson.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>Mark completed</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2 }}>
        <DialogContentText>
          {label}. The tutor is paid for it and the learner keeps the charge, as for any
          completed lesson; both are notified. If the lesson did not happen, use Cancel as
          staff instead.
        </DialogContentText>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Back
        </Button>
        <Button onClick={submit} variant="contained" disabled={busy}>
          {busy ? "Completing…" : "Mark completed"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const OpenTicketDialog = ({ open, onClose, booking, userId, label, personName, actingUser, onDone }) => {
  const [category, setCategory] = useState("other");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const trimmed = text.trim();
  const valid = trimmed.length > 0 && trimmed.length <= TICKET_TEXT_MAX;
  const reset = () => {
    setCategory("other");
    setText("");
    setError("");
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };
  const submit = async () => {
    const targetUserId = booking?.studentId || userId;
    if (!valid || !targetUserId) return;
    setBusy(true);
    setError("");
    try {
      const result = await staffCreateTicket({
        userId: targetUserId,
        category,
        text: trimmed,
        bookingId: booking?.id || null,
      });
      const number = result?.number ? `#${result.number}` : result?.ticketId;
      await logUserActivity({
        ...actor(actingUser),
        action: "TICKET_OPEN",
        entityType: "ticket",
        entityId: result?.ticketId,
        entityName: number,
        after: { userId: targetUserId, bookingId: booking?.id || null, category },
        description: `${actingUser?.name || "User"} opened ticket ${number} for ${personName}${label ? ` about ${label}` : ""}`,
      });
      reset();
      onClose();
      onDone(`Ticket ${number} opened for ${personName}.`, result?.ticketId);
    } catch (e) {
      setError(e.message || "Could not open the ticket.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Open a ticket for {personName}</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
        <DialogContentText>
          {label ? `About ${label}. ` : ""}
          {personName} sees it under Support in the app and is told of every reply.
        </DialogContentText>
        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            label="Category"
            onChange={(e) => setCategory(e.target.value)}
            disabled={busy}
          >
            {TICKET_CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="First message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={3}
          disabled={busy}
          helperText={`${trimmed.length}/${TICKET_TEXT_MAX}`}
          error={trimmed.length > TICKET_TEXT_MAX}
          autoFocus
        />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Back
        </Button>
        <Button onClick={submit} variant="contained" disabled={!valid || busy}>
          {busy ? "Opening…" : "Open ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// The ⋯ on a lesson (047 D96, D99): a list row and the lesson page's head open the same four
// doors, for the roles the drawer had (EDIT_LESSONS; ADJUST_WALLET for Credit / refund). Nothing
// is drawn when the reader may use none. `onDone(message, severity)` runs after a door changed
// something: a cancel or a finish (the caller shows the message and reads the lesson again), a
// credit (the dialog shows its own message), or a wallet that could not be read ("error"). Open
// ticket goes to the new ticket. `head` draws the page head's bordered button, else a row's.
export const LessonMenu = ({ booking, label, personName, actingUser, onDone, head = false }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [door, setDoor] = useState(null);
  const [student, setStudent] = useState(null);
  const can = (p) => hasPermission(actingUser, p);
  const doors = staffActions(booking);
  const items = [
    can(permissions.EDIT_LESSONS) && doors.cancel && { key: "cancel", label: "Cancel as staff" },
    can(permissions.EDIT_LESSONS) && doors.finish && { key: "finish", label: "Mark completed" },
    can(permissions.ADJUST_WALLET) && { key: "credit", label: "Credit / refund" },
    can(permissions.EDIT_LESSONS) && { key: "ticket", label: "Open ticket" },
  ].filter(Boolean);
  if (!booking || items.length === 0) return null;

  const menuId = `lesson-menu-${booking.id}`;
  const buttonId = `${menuId}-button`;
  const choose = async (key) => {
    setAnchor(null);
    if (key === "credit") {
      const found = await getUserById(booking.studentId);
      if (!found) {
        onDone?.("Could not read the student's wallet. Nothing has been changed.", "error");
        return;
      }
      setStudent(found);
    }
    setDoor(key);
  };
  const close = () => setDoor(null);
  const buttonProps = {
    id: buttonId,
    onClick: (e) => setAnchor(e.currentTarget),
    "aria-haspopup": "menu",
    "aria-expanded": Boolean(anchor),
    "aria-controls": anchor ? menuId : undefined,
  };
  return (
    <>
      {head ? (
        <SmallButton tone="outline" Icon={RiMoreLine} label="Lesson actions" sx={{ p: "9.5px" }} {...buttonProps} />
      ) : (
        <RowAction Icon={RiMore2Line} label={`Actions for ${label}`} {...buttonProps} />
      )}
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ list: { "aria-labelledby": buttonId } }}
      >
        {items.map((item) => (
          <MenuItem key={item.key} onClick={() => choose(item.key)}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
      <CancelAsStaffDialog open={door === "cancel"} onClose={close} booking={booking} label={label} actingUser={actingUser} onDone={(m) => onDone?.(m)} />
      <MarkFinishedDialog open={door === "finish"} onClose={close} booking={booking} label={label} actingUser={actingUser} onDone={(m) => onDone?.(m)} />
      <OpenTicketDialog
        open={door === "ticket"}
        onClose={close}
        booking={booking}
        label={label}
        personName={personName}
        actingUser={actingUser}
        onDone={(m, ticketId) => (ticketId ? navigate(`/inbox/${ticketId}`) : onDone?.(m))}
      />
      <AdjustBalanceDialog open={door === "credit"} onClose={close} person={student} actingUser={actingUser} onAdjusted={() => onDone?.()} />
    </>
  );
};
